import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import authService from '../services/authService';
import { debugConfig, debugUrlParams } from '../utils/debugConfig';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const initializedRef = useRef(false);

  // Logout
  const logout = useCallback(async () => {
    try {
      console.log('AuthContext: Iniciando logout...');
      setLoading(true);
      await authService.logout();
      console.log('AuthContext: Logout del servidor completado');
      setUser(null);
      setError(null);
      // Limpiar cualquier estado persistente
      localStorage.removeItem('auth_token');
      sessionStorage.clear();
      // Resetear el flag de inicialización para permitir nueva autenticación
      initializedRef.current = false;
      console.log('AuthContext: Estado local limpiado');
    } catch (error) {
      console.error('Error en logout:', error);
      // Aún así limpiar el estado local
      setUser(null);
      setError(null);
      localStorage.removeItem('auth_token');
      sessionStorage.clear();
      // Resetear el flag de inicialización para permitir nueva autenticación
      initializedRef.current = false;
      console.log('AuthContext: Estado local limpiado después del error');
    } finally {
      setLoading(false);
      console.log('AuthContext: Logout completado');
    }
  }, []);

  // Cargar perfil del usuario
  const loadUserProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const userData = await authService.getProfile();
      
      // Asegurar que el usuario tenga un array de roles
      if (userData && !userData.roles) {
        userData.roles = [];
      } else if (userData && !Array.isArray(userData.roles)) {
        userData.roles = Array.isArray(userData.roles) ? userData.roles : [userData.roles];
      }
      
      setUser(userData);
    } catch (error) {
      console.error('Error cargando perfil:', error);
      
      // Solo limpiar autenticación si el error es específicamente de token expirado
      if (error.message.includes('expirado') || error.message.includes('expired') || 
          (error.message.includes('401') && error.message.includes('token'))) {
        console.warn('Token expirado, limpiando autenticación');
        logout();
      } else {
        // Para otros errores (red, servidor, etc.), mantener el token pero mostrar error
        setError('Error cargando perfil. Intenta refrescar la página.');
      }
    } finally {
      setLoading(false);
    }
  }, [logout]);

  // Manejar callback de Google
  const handleGoogleCallback = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { user: userData } = await authService.handleGoogleCallback();
      
      // Asegurar que el usuario tenga un array de roles
      if (userData && !userData.roles) {
        userData.roles = [];
      } else if (userData && !Array.isArray(userData.roles)) {
        userData.roles = Array.isArray(userData.roles) ? userData.roles : [userData.roles];
      }
      
      setUser(userData);
      
      // Limpiar URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } catch (error) {
      console.error('Error en callback de Google:', error);
      setError(error.message);
      // Si hay error, limpiar autenticación
      logout();
    } finally {
      setLoading(false);
    }
  }, [logout]);

  // Verificar si hay un callback de Google al cargar
  useEffect(() => {
    if (initializedRef.current) return; // Evitar ejecución múltiple
    
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    // const state = urlParams.get('state'); // No se usa actualmente
    
    initializedRef.current = true;
    
    if (code) {
      handleGoogleCallback();
    } else if (authService.isAuthenticated()) {
      loadUserProfile();
    } else {
      setLoading(false);
    }
  }, [handleGoogleCallback, loadUserProfile, loading]); // Solo ejecutar una vez al montar

  // Efecto para manejar cambios en el estado de autenticación
  useEffect(() => {
    // Si no hay usuario y no está cargando, asegurar que loading sea false
    if (!user && !loading) {
      console.log('AuthContext: Usuario no autenticado, mostrando landing page');
    }
  }, [user, loading]);

  // Función para verificar si está autenticado basándose solo en el token
  const isAuthenticated = useCallback(() => {
    return authService.isAuthenticated();
  }, []);

  // Iniciar login con Google
  const login = async () => {
    try {
      setError(null);
      await authService.startGoogleLogin();
    } catch (error) {
      console.error('Error iniciando login:', error);
      setError(error.message);
    }
  };

  // Registrar usuario
  const register = async (role = null, siteId = null) => {
    try {
      setError(null);
      const { user: userData } = await authService.register(
        // Estos parámetros se obtendrán del callback de Google
        null, // code
        null, // state
        role,
        siteId
      );
      
      // Asegurar que el usuario tenga un array de roles
      if (userData && !userData.roles) {
        userData.roles = [];
      } else if (userData && !Array.isArray(userData.roles)) {
        userData.roles = Array.isArray(userData.roles) ? userData.roles : [userData.roles];
      }
      
      setUser(userData);
    } catch (error) {
      console.error('Error en registro:', error);
      setError(error.message);
    }
  };

  // Verificar si el usuario tiene un rol específico
  const hasRole = (role) => {
    if (!user || !user.roles || !Array.isArray(user.roles)) {
      return false;
    }
    
    return user.roles.includes(role);
  };

  // Verificar si el usuario tiene acceso a un site
  const hasSiteAccess = (siteId) => {
    if (!user || !user.sites) return false;
    return user.sites.includes(siteId);
  };

  // Verificar si el usuario tiene cualquiera de los roles especificados
  const hasAnyRole = (roles) => {
    if (!user || !user.roles || !Array.isArray(user.roles) || !Array.isArray(roles)) {
      return false;
    }
    
    return roles.some(role => user.roles.includes(role));
  };

  // Obtener sites del usuario
  const getUserSites = () => {
    if (!user || !user.sites) return [];
    return user.sites;
  };

  // Obtener roles del usuario
  const getUserRoles = () => {
    if (!user || !user.roles || !Array.isArray(user.roles)) {
      return [];
    }
    
    return user.roles;
  };

  const value = {
    user,
    loading,
    error,
    isAuthenticated: isAuthenticated(),
    login,
    register,
    logout,
    hasRole,
    hasSiteAccess,
    hasAnyRole,
    getUserSites,
    getUserRoles,
    loadUserProfile,
    clearError: () => setError(null),
    // Funciones de debugging (solo en desarrollo)
    debugConfig: process.env.NODE_ENV === 'development' ? debugConfig : undefined,
    debugUrlParams: process.env.NODE_ENV === 'development' ? debugUrlParams : undefined
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
